import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  onAuthStateChanged, 
  signInWithPopup, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail,
  signOut 
} from 'firebase/auth';
import { auth, googleProvider, isFirebaseConfigured } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Demo user for local testing if Firebase credentials are not yet configured
  const demoUser = {
    uid: 'demo_user_123',
    email: 'biohacker@protocol.local',
    displayName: 'Demo Protocol User',
    photoURL: null,
    isDemo: true
  };

  useEffect(() => {
    if (!isFirebaseConfigured) {
      // Auto enable demo mode if env vars are missing
      setIsDemoMode(true);
      setCurrentUser(demoUser);
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        setIsDemoMode(false);
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const loginWithGoogle = async () => {
    if (!isFirebaseConfigured) {
      setIsDemoMode(true);
      setCurrentUser(demoUser);
      return demoUser;
    }
    const result = await signInWithPopup(auth, googleProvider);
    setIsDemoMode(false);
    return result.user;
  };

  const loginWithEmail = async (email, password) => {
    if (!isFirebaseConfigured) {
      setIsDemoMode(true);
      setCurrentUser(demoUser);
      return demoUser;
    }
    const result = await signInWithEmailAndPassword(auth, email, password);
    setIsDemoMode(false);
    return result.user;
  };

  const signupWithEmail = async (email, password) => {
    if (!isFirebaseConfigured) {
      setIsDemoMode(true);
      setCurrentUser(demoUser);
      return demoUser;
    }
    const result = await createUserWithEmailAndPassword(auth, email, password);
    setIsDemoMode(false);
    return result.user;
  };

  const resetPassword = async (email) => {
    if (!isFirebaseConfigured) {
      // Demo mode fallback - simulate successful reset dispatch
      return true;
    }
    return sendPasswordResetEmail(auth, email.trim());
  };

  const enableDemoMode = () => {
    setIsDemoMode(true);
    setCurrentUser(demoUser);
  };

  const logout = async () => {
    if (isDemoMode) {
      setCurrentUser(null);
      setIsDemoMode(false);
      return;
    }
    await signOut(auth);
  };

  const value = {
    currentUser,
    isDemoMode,
    isFirebaseConfigured,
    loginWithGoogle,
    loginWithEmail,
    signupWithEmail,
    resetPassword,
    enableDemoMode,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
